import { z } from "zod";
import {
  DepreciationConvention,
  DepreciationMethod,
} from "~/lib/depreciation/depreciation";

export { DepreciationConvention, DepreciationMethod };

export const DepreciationMethods = Object.values(DepreciationMethod).map(
  (value) => ({ value, label: value }),
);

export const DepreciationConventions = Object.values(
  DepreciationConvention,
).map((value) => ({ value, label: value }));

// ── Domain schema (DB / server truth) ─────────────────────────────────────────

export const FixedAssetSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  name: z.string(),
  description: z.string().optional(),
  purchaseDate: z.string().optional(),
  depreciationStartDate: z.string().optional(),
  purchaseCost: z.number().optional(),
  salvageValue: z.number().optional(),
  usefulLifeYears: z.number().optional(),
  depreciationMethod: z.nativeEnum(DepreciationMethod).optional(),
  depreciationConvention: z.nativeEnum(DepreciationConvention).optional(),
  assetAccountId: z.string().optional(),
  contraAccountId: z.string().optional(),
  expenseAccountId: z.string().optional(),
  accumulatedDepreciation: z.number(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});

export type FixedAsset = z.infer<typeof FixedAssetSchema>;

export const CreateFixedAssetSchema = FixedAssetSchema.omit({
  id: true,
  businessId: true,
  createdAt: true,
  updatedAt: true,
  accumulatedDepreciation: true,
});

export const UpdateFixedAssetSchema = CreateFixedAssetSchema.partial();

export type CreateFixedAssetInput = z.infer<typeof CreateFixedAssetSchema>;
export type UpdateFixedAssetInput = z.infer<typeof UpdateFixedAssetSchema>;

// ── Form schema (UI layer) ────────────────────────────────────────────────────

export const FixedAssetFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  purchaseDate: z.string().optional(),
  depreciationStartDate: z.string().optional(),
  purchaseCost: z.string().optional(),
  salvageValue: z.string().optional(),
  usefulLifeYears: z.string().optional(),
  depreciationMethod: z.nativeEnum(DepreciationMethod).optional(),
  depreciationConvention: z.nativeEnum(DepreciationConvention).optional(),
  assetAccountId: z.string().optional(),
  contraAccountId: z.string().optional(),
  expenseAccountId: z.string().optional(),
});

export type FixedAssetForm = z.infer<typeof FixedAssetFormSchema>;

// ── Mapping: Form → Domain ────────────────────────────────────────────────────

export function formToDomain(input: FixedAssetForm): UpdateFixedAssetInput {
  return {
    name: input.name,
    description: input.description,
    purchaseDate: input.purchaseDate || undefined,
    depreciationStartDate: input.depreciationStartDate || undefined,
    purchaseCost: input.purchaseCost
      ? parseFloat(input.purchaseCost.replace(/,/g, ""))
      : undefined,
    salvageValue: input.salvageValue
      ? parseFloat(input.salvageValue.replace(/,/g, ""))
      : undefined,
    usefulLifeYears: input.usefulLifeYears
      ? parseFloat(input.usefulLifeYears.replace(/,/g, ""))
      : undefined,
    depreciationMethod: input.depreciationMethod,
    depreciationConvention: input.depreciationConvention,
    assetAccountId: input.assetAccountId || undefined,
    contraAccountId: input.contraAccountId || undefined,
    expenseAccountId: input.expenseAccountId || undefined,
  };
}

export function domainToForm(item: FixedAsset): FixedAssetForm {
  return {
    name: item.name,
    description: item.description,
    purchaseDate: item.purchaseDate ?? "",
    depreciationStartDate: item.depreciationStartDate ?? "",
    purchaseCost: item.purchaseCost != null ? String(item.purchaseCost) : "",
    salvageValue: item.salvageValue != null ? String(item.salvageValue) : "",
    usefulLifeYears:
      item.usefulLifeYears != null ? String(item.usefulLifeYears) : "",
    depreciationMethod: item.depreciationMethod,
    depreciationConvention: item.depreciationConvention,
    assetAccountId: item.assetAccountId,
    contraAccountId: item.contraAccountId,
    expenseAccountId: item.expenseAccountId,
  };
}
